import type { CoordinationData, CoordinationSettingsData } from "../../../../models/optimus-configuration.ts";
import dayjs from "dayjs";

/**
 * Formats date range for display in error messages
 * @param validFrom - Start date string or null
 * @param validTo - End date string, null, or "infinity"
 * @returns Object with formatted validFrom and validTo strings
 */
const formatDateRange = (validFrom: string | null | undefined, validTo: string | null | undefined) => {
	const formattedValidFrom = validFrom ? dayjs.utc(validFrom).format('MMM D, YYYY') : "null";
	let formattedValidTo: string;

	if (validTo === "infinity") {
		formattedValidTo = 'Present';
	} else {
		formattedValidTo = validTo ? dayjs.utc(validTo).format('MMM D, YYYY') : "Present";
	}

	return {
		validFrom: formattedValidFrom,
		validTo: formattedValidTo
	};
};

export const CoordinationSettingsErrors = Object.freeze({
	VALUE_TOO_HIGH: "value_too_high",
	VALUE_TOO_LOW: "value_too_low",
	POSITIVE_VALUE: "positive_value",
	REQUIRED_VALUE: "required_value",
	OFFSET_TOO_HIGH: "offset_too_high",
	OFFSET_TOO_LOW: "offset_too_low",
	DUPLICATE_PHASE: "duplicate_phase",
	DUPLICATE_PLAN_NUMBER: "duplicate_plan_number",
	FORCE_OFF_POINT_ERROR: "force_off_point_error",
	CYCLE_LENGTH_ERROR: "cycle_length_error",
	CYCLE_LENGTH_TOO_HIGH: "cycle_length_too_high",
	DATE_OVERLAP: "date_overlap",
	MISSING_ENABLED_PHASES: "missing_enabled_phases",
});

interface FormatErrorArgs {
	patternNumber: number | string;
	inputName?: string;
	error: string;
	ringName?: string;
	sumValue?: number;
	timeDifference?: string;
	direction?: string;
	userResponse?: string | null;
	validFrom?: string;
	validTo?: string | null;
	
}

const formatError = ({
	patternNumber,
	inputName,
	error,
	ringName,
	sumValue,
	timeDifference,
	direction,
	userResponse,
	validFrom,
	validTo,
}: FormatErrorArgs) => {
	const displayName = inputName === "offset" ? "Offset"
		: inputName === "cycle" ? 'Cycle Length'
		: inputName;

	const now = new Date();
	let status = 'Active';
	if (validFrom && validTo) {
		const from = new Date(validFrom);
		const to = new Date(validTo);
		if (from > now) {
			status = 'Upcoming';
		} else if (to < now) {
			status = 'Archived';
		}
	}

	const formattedDates = formatDateRange(validFrom, validTo);
	validFrom = formattedDates.validFrom;
	validTo = formattedDates.validTo;

	let dateInfo = ``;
	if (validFrom && validTo) {
		
		dateInfo = ` ${validFrom} to ${validTo} (${status})`;
	}

	switch (error) {
		case CoordinationSettingsErrors.VALUE_TOO_HIGH:
			return `Pattern # ${patternNumber}${dateInfo}: ${ringName} - Value too high: ${sumValue ?? ""}`;
		case CoordinationSettingsErrors.VALUE_TOO_LOW:
			return `Pattern # ${patternNumber}${dateInfo}: ${ringName} - Value too low: ${sumValue ?? ""} `;
		case CoordinationSettingsErrors.POSITIVE_VALUE:
			return `Pattern # ${patternNumber}: ${displayName} - Value must be positive`;
		case CoordinationSettingsErrors.REQUIRED_VALUE:
			return `Pattern # ${patternNumber}: ${displayName} - Value is required`;
		case CoordinationSettingsErrors.OFFSET_TOO_HIGH:
			return `Pattern # ${patternNumber}${dateInfo}: Offset - Offset must be between 0 and cycle length - 1`;
		case CoordinationSettingsErrors.OFFSET_TOO_LOW:
			return `Pattern # ${patternNumber}${dateInfo}: Offset - Offset must be greater than 0 from `;
		case CoordinationSettingsErrors.DUPLICATE_PHASE:
			return `Pattern # ${patternNumber}${dateInfo}: Phase Sequence - Phase ${inputName} appears multiple times`;
		case CoordinationSettingsErrors.DUPLICATE_PLAN_NUMBER:
			return `Pattern # ${patternNumber}: Pattern # must be unique`;
		case CoordinationSettingsErrors.FORCE_OFF_POINT_ERROR:
			const baseMessage = `Force-Off Point Error: Based on our analysis the actual force off point is ${timeDifference} seconds ${direction} than expected. This may be because of an incorrectly entered timing parameter (Offset, Offset Reference Point, Splits, or Sequence) or clock drift.`;
			if (userResponse) {
				return `${baseMessage} User selected: ${userResponse}`;
			}
			return baseMessage;
		case CoordinationSettingsErrors.CYCLE_LENGTH_ERROR:
			const cycleMessage = `Cycle Length Error: Our algorithm detected a difference between the stated cycle length and our probe-estimated cycle length.`;
			if (userResponse) {
				return `${cycleMessage} User selected: ${userResponse}`;
			}
			return cycleMessage;
		case CoordinationSettingsErrors.CYCLE_LENGTH_TOO_HIGH:
			return `Pattern # ${patternNumber}${dateInfo}: Cycle Length - Value must be at most 300.`;
		case CoordinationSettingsErrors.DATE_OVERLAP:
			return `Pattern # ${patternNumber}: Date range overlaps with another plan with the same pattern number`;
		case CoordinationSettingsErrors.MISSING_ENABLED_PHASES:
			return `Pattern # ${patternNumber}${dateInfo}: Phase Sequence - Missing enabled phases: ${inputName}`;
		default:
			return `Unhandled Error`;
	}
};

const clearRingErrorsForPlan = (currentErrors: Record<string, string>, coordinationSettingsId: string) => {
	Object.keys(currentErrors).forEach((key) => {
		if (key.startsWith(`${coordinationSettingsId}-`) && key.includes("-rings")) {
			delete currentErrors[key];
		}
	});
};

/**
 * Validates that the sum of phase splits doesn't exceed the cycle length
 */

export interface IValidateBarrierMaxSumArgs {
	coordinationSettingsExternalId: string;
	phasePlans: CoordinationData;
	currentErrors: Record<string, string>;
	coordinationSettingsId: string;
	validFrom?: string;
	validTo?: string;
}
export const validateBarrierMaxSum = ({
	coordinationSettingsExternalId,
	phasePlans,
	currentErrors,
	coordinationSettingsId,
	validFrom,
	validTo,
}: IValidateBarrierMaxSumArgs) => {
	const { cycle, splits = {}, phaseSequence, activePhases } = phasePlans;
	const ring1 = phaseSequence?.ring_1 || [];
	const ring2 = phaseSequence?.ring_2 || [];
	const enabledSet = activePhases ? new Set(activePhases) : null;

	const validFromKey = validFrom || 'null';
	const validToKey = validTo || 'infinity';
	const errorKey = `${coordinationSettingsId}-barrier-max-sum-${validFromKey}-${validToKey}`;
	delete currentErrors[errorKey];

	const isEnabled = (p: number) => p !== 0 && p !== -1 && (!enabledSet || enabledSet.has(p)) && (splits[p] || 0) > 0;
	const getSplitSum = (phases: number[]) => phases.reduce((sum, phase) => sum + (splits[phase] || 0), 0);

	const topLeft = ring1.slice(0, 2).filter(isEnabled);
	const topRight = ring1.slice(-2).filter(isEnabled);
	const bottomLeft = ring2.slice(0, 2).filter(isEnabled);
	const bottomRight = ring2.slice(-2).filter(isEnabled);

	const leftMax = Math.max(getSplitSum(topLeft), getSplitSum(bottomLeft));
	const rightMax = Math.max(getSplitSum(topRight), getSplitSum(bottomRight));
	const total = leftMax + rightMax;

	let errorType: string | null = null;

	if (total > cycle) {
		errorType = CoordinationSettingsErrors.VALUE_TOO_HIGH;
	} else if (total < cycle) {
		errorType = CoordinationSettingsErrors.VALUE_TOO_LOW;
	}

	if (errorType) {
		currentErrors[errorKey] = formatError({
			patternNumber: coordinationSettingsExternalId,
			inputName: "Barrier Max",
			error: errorType,
			ringName: "Ring sum",
			sumValue: total,
			validFrom,
			validTo,
		});
		return false;
	}

	return true;
};

export const validateRingBarrierPairs = ({
	phasePlans,
	currentErrors,
	coordinationSettingsExternalId,
	coordinationSettingsId,
	validFrom,
	validTo,
}: IValidateBarrierMaxSumArgs) => {
	const ring1 = phasePlans.phaseSequence?.ring_1 || [];
	const ring2 = phasePlans.phaseSequence?.ring_2 || [];
	const splits = phasePlans.splits || {};
	const activePhases = phasePlans.activePhases;
	const enabledSet = activePhases ? new Set(activePhases) : null;

	const isEnabled = (p: number) => p !== 0 && (!enabledSet || enabledSet.has(p)) && (Number(splits[p]) || 0) > 0;
	const sum = (phases: number[]) => phases.filter(isEnabled).reduce((acc, phase) => acc + (Number(splits[phase]) || 0), 0);

	const filterOutDisabled = (phases: number[]) => phases.filter(isEnabled);

	const validFromKey = validFrom || 'null';
	const validToKey = validTo || 'infinity';
	const firstPairErrorKey = `${coordinationSettingsId}-ring-barrier-pair-1-${validFromKey}-${validToKey}`;
	const lastPairErrorKey = `${coordinationSettingsId}-ring-barrier-pair-2-${validFromKey}-${validToKey}`;

	delete currentErrors[firstPairErrorKey];
	delete currentErrors[lastPairErrorKey];

	if (ring1.length < 2 || ring2.length < 2) {
		return true;
	}

	const r1First = ring1.slice(0, 2);
	const r2First = ring2.slice(0, 2);
	const isR1FirstPairEmpty = filterOutDisabled(r1First).length === 0;
	const isR2FirstPairEmpty = filterOutDisabled(r2First).length === 0;

	let firstPairValid = true;
	if (!isR1FirstPairEmpty && !isR2FirstPairEmpty) {
		const r1FirstSum = sum(r1First);
		const r2FirstSum = sum(r2First);

		if (r1FirstSum !== r2FirstSum) {
			let currentStatus = 'Active';
			const now = new Date();
			if (validFrom && validTo) {
				if (new Date(validFrom) > now) {
					currentStatus = 'Upcoming';
				} else if (new Date(validTo) < now) {
					currentStatus = 'Archived';
				}
			}
			const formattedDates = formatDateRange(validFrom, validTo);

			currentErrors[firstPairErrorKey] =
				`Pattern # ${coordinationSettingsExternalId} ${formattedDates.validFrom} to ${formattedDates.validTo} (${currentStatus}): First barrier pair split sums do not match — ` +
				`Ring 1 (${filterOutDisabled(r1First).join(", ")}), ` +
				`Ring 2 (${filterOutDisabled(r2First).join(", ")}) `;
			firstPairValid = false;
		}
	}

	const r1Last = ring1.slice(-2);
	const r2Last = ring2.slice(-2);
	const isR1LastPairEmpty = filterOutDisabled(r1Last).length === 0;
	const isR2LastPairEmpty = filterOutDisabled(r2Last).length === 0;

	let lastPairValid = true;
	if (!isR1LastPairEmpty && !isR2LastPairEmpty) {
		const r1LastSum = sum(r1Last);
		const r2LastSum = sum(r2Last);

		if (r1LastSum !== r2LastSum) {
			let currentStatus = 'Active';
			const now = new Date();
			if (validFrom && validTo) {
				if (new Date(validFrom) > now) {
					currentStatus = 'Upcoming';
				} else if (new Date(validTo) < now) {
					currentStatus = 'Archived';
				}
			}
			const formattedDates = formatDateRange(validFrom, validTo);

			currentErrors[lastPairErrorKey] =
				`Pattern # ${coordinationSettingsExternalId} ${formattedDates.validFrom} to ${formattedDates.validTo} (${currentStatus}): Last barrier pair split sums do not match — ` +
				`Ring 1 (${filterOutDisabled(r1Last).join(", ")}), ` +
				`Ring 2 (${filterOutDisabled(r2Last).join(", ")}) `;
			lastPairValid = false;
		}
	}

	return firstPairValid && lastPairValid;
};

// Updated to handle new phaseSequence structure
const validateAllNumbers = (
	phasePlans: CoordinationData,
	currentErrors: Record<string, string>,
	coordinationSettingsExternalId: string,
	coordinationSettingsId: string,
	validFrom?: string,
	validTo?: string
) => {
	const { cycle, offset, phaseSequence, activePhases } = phasePlans;
	
	const validFromKey = validFrom || 'null';
	const validToKey = validTo || 'infinity';

	// Cycle must be positive
	if (cycle < 0) {
		const errorKey = `${coordinationSettingsId}-cycle-${validFromKey}-${validToKey}`;
		currentErrors[errorKey] = formatError({
			patternNumber: coordinationSettingsExternalId,
			inputName: "cycle",
			error: CoordinationSettingsErrors.POSITIVE_VALUE,
			validFrom,
			validTo,
		});
		return false;
	}
	// Cycle must be <= 300
	if (cycle > 300) {
		const errorKey = `${coordinationSettingsId}-cycle-${validFromKey}-${validToKey}`;
		currentErrors[errorKey] = formatError({
			patternNumber: coordinationSettingsExternalId,
			inputName: "cycle",
			error: CoordinationSettingsErrors.CYCLE_LENGTH_TOO_HIGH,
			validFrom,
			validTo,
		});
		return false;
	}

	// Offset must be positive
	if (offset < 0) {
		const errorKey = `${coordinationSettingsId}-offset-${validFromKey}-${validToKey}`;
		currentErrors[errorKey] = formatError({
			patternNumber: coordinationSettingsExternalId,
			inputName: "offset",
			error: CoordinationSettingsErrors.POSITIVE_VALUE,
			validFrom,
			validTo,
		});
		return false;
	}

	// Use activePhases if available, otherwise extract phases from phaseSequence
	const phasesToValidate =
		activePhases || (phaseSequence ? [...(phaseSequence.ring_1 || []), ...(phaseSequence.ring_2 || [])] : []);

	// Filter out any -1 values (which represent empty phases)
	const validPhases = phasesToValidate.filter((phase) => phase !== -1);

	// Validate each phase's split value
	for (const phase of validPhases) {
		const value = phasePlans.splits[phase];

		const errorKey = `${coordinationSettingsId}-${phase}-${validFromKey}-${validToKey}`;

		// Split must be positive
		if (value < 0) {
			currentErrors[errorKey] = formatError({
				patternNumber: coordinationSettingsExternalId,
				inputName: `Phase ${phase}`,
				error: CoordinationSettingsErrors.POSITIVE_VALUE,
				validFrom,
				validTo,
			});
			return false;
		}

		// Split is required
		if (!value && value !== 0) {
			currentErrors[errorKey] = formatError({
				patternNumber: coordinationSettingsExternalId,
				inputName: `Phase ${phase}`,
				error: CoordinationSettingsErrors.REQUIRED_VALUE,
				validFrom,
				validTo,
			});
			return false;
		}

		delete currentErrors[errorKey];
	}

	return true;
};

interface ValidatePhaseSequenceArgs {
	phasePlans: CoordinationData;
	currentErrors: {
		[key: string]: string;
	};
	coordinationSettingsExternalId: string;
	coordinationSettingsId: string;
	validFrom?: string;
	validTo?: string;
}
// Completely revise the validatePhaseSequence function
const validatePhaseSequence = ({
	phasePlans,
	currentErrors,
	coordinationSettingsExternalId,
	coordinationSettingsId,
	validFrom,
	validTo,
}: ValidatePhaseSequenceArgs) => {
	const { phaseSequence } = phasePlans;

	if (!phaseSequence) {
		return true;
	}
	
	const validFromKey = validFrom || 'null';
	const validToKey = validTo || 'infinity';

	// Combine phases from both rings
	const allPhases = [...(phaseSequence.ring_1 || []), ...(phaseSequence.ring_2 || [])].filter(
		(phase) => phase !== -1
	); // Filter out placeholder phases

	// Find duplicates
	const phaseCounts: Record<number, number> = {};
	allPhases.forEach((phase) => {
		phaseCounts[phase] = (phaseCounts[phase] || 0) + 1;
	});

	// Clear all existing duplicate errors first
	Object.keys(currentErrors).forEach((key) => {
		const rowErrorKey = `${coordinationSettingsId}-duplicate-${validFromKey}-${validToKey}`;
		if (key.includes(rowErrorKey)) {
			delete currentErrors[key];
		}
	});

	// Add errors for duplicates
	let isValid = true;
	Object.entries(phaseCounts).forEach(([phaseKey, count]) => {
		const phase = Number(phaseKey); // ensure numeric
		if (count > 1 && phase !== 0) {
			const errorKey = `${coordinationSettingsId}-duplicate-phase-${phase}-${validFromKey}-${validToKey}`;
			currentErrors[errorKey] = formatError({
				patternNumber: coordinationSettingsExternalId,
				inputName: String(phase), // or phase if formatError accepts number
				error: CoordinationSettingsErrors.DUPLICATE_PHASE,
				validFrom,
				validTo,
			});
			isValid = false;
		}
	});

	// Validate that all enabled phases are included in the phase sequence
	const presentPhases = new Set(allPhases.filter((p) => p !== 0 && p !== -1));
	const enabledPhases = (phasePlans.activePhases || []).filter((p) => p !== 0 && p !== -1);

	// Clear previous missing-enabled-phases error
	const missingErrorKey = `${coordinationSettingsId}-missing-enabled-phases-${validFromKey}-${validToKey}`;
	delete currentErrors[missingErrorKey];

	const missing = enabledPhases.filter((p) => !presentPhases.has(p));
	if (missing.length > 0) {
		currentErrors[missingErrorKey] = formatError({
			patternNumber: coordinationSettingsExternalId,
			inputName: missing.join(", "),
			error: CoordinationSettingsErrors.MISSING_ENABLED_PHASES,
			validFrom,
			validTo,
		});
		isValid = false;
	}

	return isValid;
};

// Helper function to check if two date ranges overlap
const doDateRangesOverlap = (
	start1: string | null,
	end1: string | null,
	start2: string | null,
	end2: string | null,
): boolean => {
	// Convert to dayjs objects for comparison
	const s1 = start1 ? dayjs(start1) : null;
	const e1 = (end1 !== "infinity" && end1) ? dayjs(end1) : null;
	const s2 = start2 ? dayjs(start2) : null;
	const e2 = (end2 !== "infinity" && end2) ? dayjs(end2) : null;

	// If either range has no start date, treat as starting from beginning of time
	// If either range has no end date, treat as ending at end of time
	const range1Start = s1 || dayjs("1900-01-01");
	const range1End = e1 || dayjs("2100-12-31");
	const range2Start = s2 || dayjs("1900-01-01");
	const range2End = e2 || dayjs("2100-12-31");

	// Ranges overlap if: start1 < end2 AND start2 < end1
	return range1Start.isBefore(range2End) && range2Start.isBefore(range1End);
};

// Function to validate date overlaps for coordination settings with the same pattern number
const validateDateOverlaps = (
	currentSetting: CoordinationSettingsData,
	allCoordinationSettingsRows: CoordinationSettingsData[],
	currentErrors: { [key: string]: string }
): void => {
	const currentPatternNumber = currentSetting.coordination_settings_external_id;
	const currentId = currentSetting.coordination_settings_id;

	// Find all other settings with the same pattern number but different IDs
	const samePatternSettings = allCoordinationSettingsRows.filter(
		(setting) =>
			setting.coordination_settings_external_id === currentPatternNumber &&
			setting.coordination_settings_id !== currentId
	);
	
	// Check for date overlaps with each setting that has the same pattern number
	samePatternSettings.forEach((otherSetting) => {
		
		if (
			doDateRangesOverlap(
				currentSetting.valid_from || null,
				currentSetting.valid_to || null,
				otherSetting.valid_from || null,
				otherSetting.valid_to || null
			)
		) {

			const errorKey = `${currentPatternNumber}-date-overlap`;
			let currentStatus = 'Active';
			let otherStatus = 'Active';
			const now = new Date();
			if (currentSetting.valid_from && currentSetting.valid_to && otherSetting.valid_from && otherSetting.valid_to) {
				if (new Date(currentSetting.valid_from) > now) {
					currentStatus = 'Upcoming';
				} else if (new Date(currentSetting.valid_to) < now) {
					currentStatus = 'Archived';
				}
				if (new Date(otherSetting.valid_from) > now) {
					otherStatus = 'Upcoming';
				} else if (new Date(otherSetting.valid_to) < now) {
					otherStatus = 'Archived';
				}
			}
			const currentSettingDates = formatDateRange(currentSetting.valid_from, currentSetting.valid_to);
			const otherSettingDates = formatDateRange(otherSetting.valid_from, otherSetting.valid_to);

			const customMessage = `Pattern # ${currentPatternNumber}: has two overlapping date ranges ${currentSettingDates.validFrom} to ${currentSettingDates.validTo} (${currentStatus}) and ${otherSettingDates.validFrom} to ${otherSettingDates.validTo} (${otherStatus})`;
			currentErrors[errorKey] = customMessage;
		}
	});
};

interface ValidateInputArgs {
	phasePlans: CoordinationData;
	currentErrors: {
		[key: string]: string;
	};
	coordinationSettingsExternalId: string;
	allCoordinationSettingsRows: CoordinationSettingsData[];
	coordinationSettingsId: string;
	validFrom?: string;
	validTo?: string;
}

export const validateInput = ({
	phasePlans,
	currentErrors,
	coordinationSettingsExternalId,
	allCoordinationSettingsRows,
	coordinationSettingsId,
	validFrom,
	validTo,
}: ValidateInputArgs) => {
	clearRingErrorsForPlan(currentErrors, coordinationSettingsExternalId);
	const { cycle, offset } = phasePlans;

	const validFromKey = validFrom || 'null';
	const validToKey = validTo || 'infinity';

	// Find the current setting for date overlap validation
	const currentSetting = allCoordinationSettingsRows.find(
		(setting) => setting.coordination_settings_id === coordinationSettingsId
	);

	// Validate date overlaps if we have the current setting
	if (currentSetting) {
		validateDateOverlaps(currentSetting, allCoordinationSettingsRows, currentErrors);
	}

	// Validate that offset is not greater than or equal to cycle
	if (offset >= cycle) {
		const errorKey = `${coordinationSettingsId}-offset-${validFromKey}-${validToKey}`;
		currentErrors[errorKey] = formatError({
			patternNumber: coordinationSettingsExternalId,
			inputName: "offset",
			error: CoordinationSettingsErrors.OFFSET_TOO_HIGH,
			validFrom,
			validTo,
		});
		return false;
	} else if (offset < 0) {
		// Fix for offset validation when creating new timing plans
		const errorKey = `${coordinationSettingsId}-offset-${validFromKey}-${validToKey}`;
		currentErrors[errorKey] = formatError({
			patternNumber: coordinationSettingsExternalId,
			inputName: "offset",
			error: CoordinationSettingsErrors.OFFSET_TOO_LOW,
			validFrom,
			validTo,
		});
		return false;
	} else {
		const errorKey = `${coordinationSettingsId}-offset-${validFromKey}-${validToKey}`;
		// Clear any existing offset error
		delete currentErrors[errorKey];
	}

	// Validate all numeric values
	const allNumbersValid = validateAllNumbers(
		phasePlans,
		currentErrors,
		coordinationSettingsExternalId,
		coordinationSettingsId,
		validFrom,
		validTo
	);
	if (!allNumbersValid) return false;

	return true;
};
